import type { ComponentProps } from 'react';
import type { Ionicons } from '@expo/vector-icons';
import type { BusinessCapability } from '../../shell/session/domain/businessProfile';
import type { OperationModeKey } from '../../shell/session/domain/operationMode';

export type OperationModeToggleDef = {
  key: OperationModeKey;
  label: string;
  explanation: string;
  hideConfirm: string;
  iconName: ComponentProps<typeof Ionicons>['name'];
  capability: BusinessCapability | null;
  planLock?: 'fiado' | 'promotions';
};

/** Fila en Organización y título de la pantalla de toggles. */
export const OPERATION_MODE_HUB_ROW = {
  title: 'Cómo operás',
  description: 'Qué ves en el menú. Stock, clientes, caja, online.',
} as const;

export const OPERATION_MODE_TOGGLES: OperationModeToggleDef[] = [
  {
    key: 'inventory',
    label: '¿Llevás stock?',
    explanation: 'Cantidades y reposición. Si lo apagás, se ocultan Stock y Compras. Los movimientos quedan.',
    hideConfirm:
      'Se ocultan Stock y Compras. Los movimientos quedan. Las ventas no van a frenar por falta de stock.',
    iconName: 'cube-outline',
    capability: 'inventory',
  },
  {
    key: 'cash_sessions',
    label: '¿Cerrás caja por turno?',
    explanation: 'Turno y arqueo. Si lo apagás, se oculta Caja. El historial queda.',
    hideConfirm: 'Se oculta Caja. Los turnos quedan.',
    iconName: 'wallet-outline',
    capability: 'cash_sessions',
  },
  {
    key: 'customer_credit_fiado',
    label: '¿Anotás clientes?',
    explanation: 'Nombres, deudas y cobros. Si lo apagás, se oculta Clientes. Las cuentas quedan.',
    hideConfirm: 'Se oculta Clientes. Los clientes quedan.',
    iconName: 'people-outline',
    capability: 'customer_credit_fiado',
    planLock: 'fiado',
  },
  {
    key: 'promotions',
    label: '¿Hacés promociones?',
    explanation: 'Descuentos del POS. Si lo apagás, se oculta Promos. Las que cargaste quedan.',
    hideConfirm: 'Se oculta Promos. Las promociones quedan.',
    iconName: 'pricetag-outline',
    capability: 'promotions',
    planLock: 'promotions',
  },
  {
    key: 'price_lists',
    label: '¿Manejás listas de precio?',
    explanation: 'Mayorista o lista especial. Si lo apagás, se oculta Listas. Las listas quedan.',
    hideConfirm: 'Se oculta Listas. Las listas quedan.',
    iconName: 'list-outline',
    capability: 'price_lists',
  },
  {
    key: 'customer_loyalty',
    label: '¿Hacés puntos / fidelización?',
    explanation: 'Puntos por compra. Si lo apagás, se oculta Fidelización. Los saldos quedan.',
    hideConfirm: 'Se oculta Fidelización. Los saldos quedan.',
    iconName: 'gift-outline',
    capability: 'customer_loyalty',
  },
  {
    key: 'expenses',
    label: '¿Registrás gastos?',
    explanation: 'Facturas y cuentas a pagar. Si lo apagás, se ocultan Gastos y Compras. Los registros quedan.',
    hideConfirm: 'Se ocultan Gastos y Compras. Los registros quedan.',
    iconName: 'receipt-outline',
    capability: 'expenses',
  },
  {
    key: 'purchases',
    label: '¿Comprás mercadería?',
    explanation: 'Órdenes de compra y recepción. Si lo apagás, se oculta Compras. Las órdenes quedan.',
    hideConfirm: 'Se oculta Compras. Las órdenes quedan.',
    iconName: 'cart-outline',
    capability: 'purchases',
  },
  {
    key: 'kitchen_tickets',
    label: '¿Tenés cocina / comanda?',
    explanation: 'Comandas. Si lo apagás, se oculta Cocina. Los tickets quedan.',
    hideConfirm: 'Se oculta Cocina. Los tickets quedan.',
    iconName: 'restaurant-outline',
    capability: 'kitchen_tickets',
  },
  {
    key: 'tables',
    label: '¿Tenés mesas / salón?',
    explanation: 'Mesas del salón. Si lo apagás, se oculta Mesas. El salón queda.',
    hideConfirm: 'Se oculta Mesas. Las mesas quedan.',
    iconName: 'grid-outline',
    capability: 'tables',
  },
  {
    key: 'appointments',
    label: '¿Usás agenda / turnos?',
    explanation: 'Turnos y reservas. Si lo apagás, se oculta Agenda. Los turnos quedan.',
    hideConfirm: 'Se oculta Agenda. Los turnos quedan.',
    iconName: 'calendar-outline',
    capability: 'appointments',
  },
  {
    key: 'work_orders',
    label: '¿Usás órdenes de trabajo?',
    explanation: 'Reparaciones y tareas. Si lo apagás, se ocultan Órdenes de trabajo. Las OT quedan.',
    hideConfirm: 'Se ocultan Órdenes de trabajo. Las OT quedan.',
    iconName: 'construct-outline',
    capability: 'work_orders',
  },
  // #900 Phase 3: optional capability toggles
  {
    key: 'delivery_fulfillment',
    label: '¿Hacés entregas?',
    explanation: 'Delivery y pedidos con envío. Si lo apagás, se ocultan las opciones de entrega. El catálogo online queda.',
    hideConfirm: 'Se ocultan las opciones de entrega. Los pedidos sin envío quedan.',
    iconName: 'bicycle-outline',
    capability: 'delivery_fulfillment',
  },
  {
    key: 'barcode_scan',
    label: '¿Escaneás códigos?',
    explanation: 'Lector de código de barras en el POS. Si lo apagás, se oculta el escáner. La búsqueda manual queda.',
    hideConfirm: 'Se oculta el escáner. La búsqueda manual queda.',
    iconName: 'scan-outline',
    capability: 'barcode_scan',
  },
  {
    key: 'product_variants',
    label: '¿Usás talles y colores?',
    explanation: 'Variantes de producto. Si lo apagás, se ocultan las variantes. Los productos quedan.',
    hideConfirm: 'Se ocultan las variantes. Los productos quedan.',
    iconName: 'color-palette-outline',
    capability: 'product_variants',
  },
  {
    key: 'weight_uom',
    label: '¿Vendés por kilo?',
    explanation: 'Balanza y precio por peso. Si lo apagás, se oculta la venta por peso. El catálogo queda.',
    hideConfirm: 'Se oculta la venta por peso. El catálogo queda.',
    iconName: 'scale-outline',
    capability: 'weight_uom',
  },
  {
    key: 'commissions',
    label: '¿Pagás comisiones?',
    explanation: 'Comisiones a vendedores. Si lo apagás, se ocultan las comisiones. Los registros quedan.',
    hideConfirm: 'Se ocultan las comisiones. Los registros quedan.',
    iconName: 'cash-outline',
    capability: 'commissions',
  },
  {
    key: 'misc_quick_sale',
    label: '¿Usás venta rápida?',
    explanation: 'Vender sin catálogo, al toque. Si lo apagás, se oculta la venta rápida. El catálogo queda.',
    hideConfirm: 'Se oculta la venta rápida. El catálogo queda.',
    iconName: 'flash-outline',
    capability: 'misc_quick_sale',
  },
  {
    key: 'external_services',
    label: '¿Vendés recargas / servicios?',
    explanation: 'Recargas y servicios externos en el POS. Si lo apagás, se oculta Recarga. El historial queda.',
    hideConfirm: 'Se oculta Recarga. El historial queda.',
    iconName: 'phone-portrait-outline',
    capability: 'external_services',
  },
  {
    key: 'menu_modifiers',
    label: '¿Usás opciones de menú?',
    explanation: 'Modificadores y extras en el catálogo. Si lo apagás, se ocultan las opciones. Los productos quedan.',
    hideConfirm: 'Se ocultan las opciones de menú. Los productos quedan.',
    iconName: 'options-outline',
    capability: 'menu_modifiers',
  },
  {
    key: 'service_catalog',
    label: '¿Vendés servicios?',
    explanation: 'Servicios en el catálogo además de productos. Si lo apagás, se oculta el tipo servicio. Los datos quedan.',
    hideConfirm: 'Se oculta el catálogo de servicios. Los datos quedan.',
    iconName: 'cut-outline',
    capability: 'service_catalog',
  },
  {
    key: 'service_quotes',
    label: '¿Hacés presupuestos?',
    explanation: 'Cotizaciones para clientes. Si lo apagás, se oculta Cotizaciones. Las cotizaciones quedan.',
    hideConfirm: 'Se oculta Cotizaciones. Las cotizaciones quedan.',
    iconName: 'document-text-outline',
    capability: 'service_quotes',
  },
  {
    key: 'service_packages',
    label: '¿Vendés paquetes de sesiones?',
    explanation: 'Paquetes con varias sesiones (ej. 4 cortes). Si lo apagás, se ocultan Paquetes. Los datos quedan.',
    hideConfirm: 'Se ocultan Paquetes. Los datos quedan.',
    iconName: 'albums-outline',
    capability: 'service_packages',
  },
  {
    key: 'split_bill',
    label: '¿Dividís la cuenta?',
    explanation: 'Dividir cuenta en el POS. Si lo apagás, se oculta la opción. Las ventas quedan.',
    hideConfirm: 'Se oculta dividir cuenta. Las ventas quedan.',
    iconName: 'git-branch-outline',
    capability: 'split_bill',
  },
  {
    key: 'pos_tips',
    label: '¿Cobrás propina en el POS?',
    explanation:
      'Propina en el cobro del mostrador o salón. Si lo apagás, no aparece al cobrar. La propina del delivery online va aparte.',
    hideConfirm: 'Se oculta la propina al cobrar. Las ventas ya hechas quedan.',
    iconName: 'heart-outline',
    capability: 'pos_tips',
  },
  {
    key: 'on_duty',
    label: '¿Tenés guardia?',
    explanation: 'Mostrar "De guardia" en la tienda online. Si lo apagás, se oculta el switch. El historial queda.',
    hideConfirm: 'Se oculta la guardia en la tienda. El historial queda.',
    iconName: 'medkit-outline',
    capability: 'on_duty',
  },
  {
    key: 'storefront',
    label: '¿Vendés online?',
    explanation: 'Pedidos por internet. Si lo apagás, se ocultan Pedidos y la tienda. El perfil queda.',
    hideConfirm: 'Se ocultan Pedidos y la tienda. El perfil queda.',
    iconName: 'storefront-outline',
    capability: null,
  },
];
